/* eslint-disable react-refresh/only-export-components */
import { useCallback, useEffect, useMemo, useState } from 'react'
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined'
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded'
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined'
import CalendarMonth from '@mui/icons-material/CalendarMonth'
import ChatBubbleOutlineOutlined from '@mui/icons-material/ChatBubbleOutlineOutlined'
import ChatBubble from '@mui/icons-material/ChatBubble'
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined'
import FactCheck from '@mui/icons-material/FactCheck'
import FolderOutlined from '@mui/icons-material/FolderOutlined'
import Folder from '@mui/icons-material/Folder'
import HistoryOutlined from '@mui/icons-material/HistoryOutlined'
import HistoryRounded from '@mui/icons-material/HistoryRounded'
import InsightsOutlined from '@mui/icons-material/InsightsOutlined'
import InsightsRounded from '@mui/icons-material/InsightsRounded'
import AnalyticsOutlined from '@mui/icons-material/AnalyticsOutlined'
import GroupsOutlined from '@mui/icons-material/GroupsOutlined'
import GroupsRounded from '@mui/icons-material/GroupsRounded'
import MapOutlined from '@mui/icons-material/MapOutlined'
import MapRounded from '@mui/icons-material/MapRounded'
import SensorsOutlined from '@mui/icons-material/SensorsOutlined'
import SensorsRounded from '@mui/icons-material/SensorsRounded'
import ViewKanbanOutlined from '@mui/icons-material/ViewKanbanOutlined'
import ViewKanban from '@mui/icons-material/ViewKanban'
import { CalendarPage } from '../components/Calendar/CalendarPage'
import { MemberCalendarScreen } from '../components/Calendar/MemberCalendarScreen'
import { ChatPage } from '../components/Chat/ChatPage'
import { MemberChatScreen } from '../components/Chat/MemberChat'
import { ForYouPage } from '../components/ForYouPage'
import { Button } from '../components/Common/Button'
import { Pressable } from '../components/Common/Pressable'
import { MobileFullScreenPanel } from '../components/Common/MobileFullScreenPanel'
import { MobileTabRow, type MobileTabItem } from '../components/Common/MobileTabs'
import { TabErrorBoundary } from '../components/Common/TabErrorBoundary'
import { XRayProvider } from '../components/Common/XRay'
import { AiMagicController } from '../components/Member/AiMagicController'
import { MemberFormsMobile } from '../components/Member/controls/MemberFormsMobile'
import { FormsOrchestrator } from '../components/Member/FormsOrchestrator'
import { MobileSyncCenter } from '../components/MobileSync/MobileSyncCenter'
import { MemberAutomationsMobile } from '../components/Member/controls/MemberAutomationsMobile'
import { AutomationLauncher } from '../components/Member/AutomationLauncher'
import { MemberPeopleMobile } from '../components/Member/controls/MemberPeopleMobile'
import { ClassTab } from '../components/Member/Class/ClassTab'
import { MemberOpsTab } from '../components/Member/ops/MemberOpsTab'
import { MemberBookingsView } from '../components/Member/bookings/MemberBookingsView'
import { InvitesHubV2 } from '../components/InvitesHubV2'
import { MemberBoard } from '../components/Member/MemberBoard'
import { MemberBoardHub, type MemberBoardSubTab } from '../components/Member/MemberBoardHub'
import { migrateMemberBoardSub } from '../components/Member/memberBoardUtils'
import { MemberChecklistDetailPage } from '../components/Member/MemberChecklistDetailPage'
import { MemberDashboardLayout } from '../components/Member/MemberDashboardLayout'
import { MemberPageComposite } from '../components/Member/MemberPageComposite'
import { MemberTodos } from '../components/Member/MemberTodos'
import { MemberMeetingsScreen } from '../components/Member/nexus/MemberMeetingsScreen'
import { MemberHuddleScreen } from '../components/Member/nexus/MemberHuddleScreen'
import { useNexusCounts } from '../components/Member/controls/useNexusCounts'
import type { MemberNexusChip } from '../components/Member/controls/MemberNexusPrimitives'
import { KeyboardHeightBridge } from '../hooks/useKeyboardHeight'
import { GhostEchoListener } from '../components/GhostEcho/GhostEchoListener'
import { RaidCelebration } from '../components/Social/RaidCelebration'
import { RaidOverlay } from '../components/Raid/RaidOverlay'
import { STATUS_META } from '../services/checklistStatusMachine'
import type { ChecklistWorkflowStatus } from '../types/schema'
import { MemberAppearanceSection } from '../components/Member/MemberAppearanceSection'
import { SideNavPageShell } from '../components/Member/sidenav/SideNavPageShell'
import { ProfileDetailsPage } from '../components/Profile/ProfileDetailsPage'
import { MemberSurfaceTheme } from './MemberSurfaceTheme'
import { BarcodeScannerShell } from '../components/Helper/BarcodeScannerShell'
import { ExitConfirmSheet } from '../components/Common/ExitConfirmSheet'
import { BackStackProvider } from '../context/BackStackContext'
import { MemberOverlayProvider } from './MemberOverlayContext'
import { useMemberOverlay } from './MemberOverlayContext'
import { MemberNodeDetailsSheet } from '../components/Member/MemberNodeDetailsSheet'
import { useNativeBackButton } from '../hooks/useNativeBackButton'
import { useFocusMode } from '../context/useFocusMode'
import { MemberWidgetCachePublisher } from '../components/Widgets/NativeWidgetCachePublisher'
import { usePersistedState } from '../hooks/usePersistedState'
import { useNativeWidgetIntent } from '../hooks/useNativeWidgetIntent'
import { requestWorkspaceSwitch } from '../services/workspaceSwitchService'
import type { NativeWidgetIntent } from '../services/nativeWidgetBridge'
import { startSyncWorker, stopSyncWorker } from '../services/helper/syncWorker'
import { queueChecklistComment, queueChecklistProof, queueChecklistToggle } from '../services/helper/queueService'
import { currentOccurrenceKeyForItem } from '../services/recurringChecklistService'
import { useWorkspaceDay } from '../hooks/useWorkspaceDay'
import { InventoryScanError, ResourceMismatchError, type StockScanDirection } from '../services/InventoryScannerService'
import { useStockScan } from '../hooks/useStockScan'
import { emitSensoryEvent } from '../services/aliveness/SensoryEventBus'
import { registerMobilePushToken } from '../services/mobilePushRegistrationService'
import { useTimeline } from '../context/useTimeline'
import { useWorkspace } from '../context/useWorkspace'
import { isChecklistGate } from '../utils/checklistDependencyState'
import { useAuth } from '../context/useAuth'
import { useToast } from '../context/useToast'
import { useIsMemberMobile } from './useIsMemberMobile'
import { useDisplayName } from '../hooks/useDisplayName'
import { useNls } from '../nls/nlsProvider'
import { getMemberAiSubtabLabel, getMemberCollaborationSubtabLabel, getMemberControlSectionLabel, getMemberTabLabel } from '../components/Mobile/MobileNls'
import type { Workspace } from '../types/workspace'
import type { ChecklistItem } from '../types/schema'
import type { TimelineElement } from '../types/timelineElement'
import type { ViewContext } from '../services/ViewContextService'
import type { BlockerCtaSection } from '../types/checklistBlockerCodes'
import {
  buildMemberPath,
  buildMemberBasePath,
  buildMemberBoardNodePath,
  buildMemberChecklistItemPath,
  isSyntheticChecklistParent,
  isMemberAiSubTab,
  resolveMemberNativeWidgetIntent,
  type MemberAiSubTab,
  type MemberCollaborationSubTab,
  type MemberRoute,
  type MemberTabKey,
  type MemberWidgetPanel,
} from './memberAppRouting'
import { resolveSwitchLanding } from '../services/workspaceSwitch/switchLanding'
import { migrateMemberAiSubStorage } from './memberAiSubMigration'
import { isPersonalWorkspace, isTeamWorkspace } from '../utils/workspaceKind'
import { featureFlag } from '../utils/featureFlags'
import { MODULE_TOAST_DURATION_MS, switchedOffModulesFor, type ModuleKey } from '../utils/workspaceModules'
import { hiddenSurfacesForModules, MODULE_TITLE_NLS_KEYS } from '../utils/workspaceModulesSurfaces'
import type { NotificationRouterCtx } from '../services/notificationRouter'
import { subscribePageVisibility, usePageVisibility } from '../services/pageVisibilityService'
import { isWorkspaceOwner } from '../utils/workspaceOwners'
import { parseChecklistDeepLink } from '../services/checklistDeepLinkService'

export { buildMemberBoardNodePath, buildMemberChecklistItemPath, isSyntheticChecklistParent, parseMemberRoute, resolveMemberNativeWidgetIntent } from './memberAppRouting'
export type { MemberAiSubTab, MemberCollaborationSubTab, MemberNativeWidgetIntentResolution, MemberRoute, MemberTabKey, MemberWidgetPanel } from './memberAppRouting'

const subIconStyle = { fontSize: 20 }

const getBoardSubItems = (t: ReturnType<typeof useNls>['t']): Array<MobileTabItem<MemberBoardSubTab>> => [
  {
    key: 'STRIPBOARD',
    label: t('NLSMemberSubtabStripboard'),
    icon: <ViewKanbanOutlined style={subIconStyle} />,
    activeIcon: <ViewKanban style={subIconStyle} />,
    testId: 'member-board-sub-stripboard',
  },
  { key: 'MANAGE', label: t('NLSMemberSubtabReview'), icon: <FactCheckOutlined style={subIconStyle} />, activeIcon: <FactCheck style={subIconStyle} />, testId: 'member-board-sub-manage' },
  { key: 'FOLDER', label: t('NLSMemberSubtabFolder'), icon: <FolderOutlined style={subIconStyle} />, activeIcon: <Folder style={subIconStyle} />, testId: 'member-board-sub-folder' },
  {
    key: 'ANALYSIS',
    label: t('NLSAnalysisMobileBoardSubItemLabel'),
    icon: <AnalyticsOutlined style={subIconStyle} />,
    activeIcon: <InsightsRounded style={subIconStyle} />,
    testId: 'member-board-sub-analysis',
  },
]

const getAiSubItems = (t: ReturnType<typeof useNls>['t']): Array<MobileTabItem<MemberAiSubTab>> => [
  { key: 'HISTORY', label: getMemberAiSubtabLabel('HISTORY', t), icon: <HistoryOutlined style={subIconStyle} />, activeIcon: <HistoryRounded style={subIconStyle} />, testId: 'member-ai-sub-history' },
  {
    key: 'CREATE',
    label: getMemberAiSubtabLabel('CREATE', t),
    icon: <AutoAwesomeOutlined style={subIconStyle} />,
    activeIcon: <AutoAwesomeRounded style={subIconStyle} />,
    testId: 'member-ai-sub-create',
  },
  { key: 'EXECUTE', label: getMemberAiSubtabLabel('EXECUTE', t), icon: <MapOutlined style={subIconStyle} />, activeIcon: <MapRounded style={subIconStyle} />, testId: 'member-ai-sub-execute' },
  { key: 'PULSE', label: getMemberAiSubtabLabel('PULSE', t), icon: <InsightsOutlined style={subIconStyle} />, activeIcon: <InsightsRounded style={subIconStyle} />, testId: 'member-ai-sub-pulse' },
  { key: 'MAGIC', label: getMemberAiSubtabLabel('MAGIC', t), icon: <AutoAwesomeOutlined style={subIconStyle} />, activeIcon: <AutoAwesomeRounded style={subIconStyle} />, testId: 'member-ai-sub-magic' },
]

// Retired Controls sub-items (Controls retired in R27 TB3 per TB-D5):
// key: 'MEMBERS', label: getMemberControlSectionLabel('MEMBERS', t)
// testId: 'member-controls-sub-members'
if (import.meta.env.DEV) {
  void getMemberControlSectionLabel
}

const getCollaborationSubItems = (
  t: ReturnType<typeof useNls>['t'],
  showMeetingsInNexus: boolean,
  inHuddleCount: number,
): Array<MobileTabItem<MemberCollaborationSubTab>> => {
  const items: Array<MobileTabItem<MemberCollaborationSubTab>> = [
    {
      key: 'CHAT',
      label: getMemberCollaborationSubtabLabel('CHAT', t),
      icon: <ChatBubbleOutlineOutlined style={subIconStyle} />,
      activeIcon: <ChatBubble style={subIconStyle} />,
      testId: 'member-nexus-sub-chat',
    },
    {
      key: 'CALENDAR',
      label: getMemberCollaborationSubtabLabel('CALENDAR', t),
      icon: <CalendarMonthOutlined style={subIconStyle} />,
      activeIcon: <CalendarMonth style={subIconStyle} />,
      testId: 'member-nexus-sub-calendar',
    },
  ]
  if (showMeetingsInNexus) {
    items.push(
      {
        key: 'HUDDLE',
        label: getMemberCollaborationSubtabLabel('HUDDLE', t),
        icon: <SensorsOutlined style={subIconStyle} />,
        activeIcon: <SensorsRounded style={subIconStyle} />,
        unread: inHuddleCount > 0,
        testId: 'member-nexus-sub-huddle',
      },
      {
        key: 'MEETINGS',
        label: getMemberCollaborationSubtabLabel('MEETINGS', t),
        icon: <GroupsOutlined style={subIconStyle} />,
        activeIcon: <GroupsRounded style={subIconStyle} />,
        testId: 'member-nexus-sub-meetings',
      },
    )
  }
  return items
}

const AppMemberInner = ({ route, workspace }: { route: MemberRoute; workspace: Workspace }) => {
  const { t } = useNls()
  const { elements, checklistItems, canPerformOnNode, pathOnlyElementIds = new Set(), checklistsLoaded } = useTimeline()
  const { top: overlayTop, openNode, openChecklist, closeTop } = useMemberOverlay()
  const { currentUser, userProfile } = useAuth()
  const { showToast } = useToast()
  const { workspaces, activeRole, canPerform, rolesByWorkspaceId } = useWorkspace()
  const memberName = useDisplayName(userProfile ?? currentUser)
  const isMobile = useIsMemberMobile()
  const personalInMemberAppEnabled = featureFlag('personalInMemberApp')
  const switchedOffModules = useMemo(() => switchedOffModulesFor(workspace), [workspace])
  const moduleHidden = useMemo(() => hiddenSurfacesForModules(switchedOffModules), [switchedOffModules])
  const notifyModuleOff = useCallback(
    (key: ModuleKey) =>
      showToast(
        t('NLSWorkspaceModuleTurnedOffToast', { module: t(MODULE_TITLE_NLS_KEYS[key]) }),
        'info',
        undefined,
        'normal',
        { durationMs: MODULE_TOAST_DURATION_MS },
      ),
    [showToast, t],
  )
  const showMeetingsInNexus = !(personalInMemberAppEnabled && isPersonalWorkspace(workspace)) && !moduleHidden.memberNexus.has('MEETINGS')
  const activeTab = route.tab
  const [queryState, setQueryState] = useState(() => new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search))
  const [selectedElement, setSelectedElement] = useState<TimelineElement | null>(null)
  const [profileOverlayOpen, setProfileOverlayOpen] = useState(activeTab === 'you')
  const [raidOverlayOpen, setRaidOverlayOpen] = useState(false)
  const [chatActionItemId, setChatActionItemId] = useState<string | null>(null)
  const [scannerItem, setScannerItem] = useState<ChecklistItem | null>(null)
  const [widgetPanel, setWidgetPanel] = useState<MemberWidgetPanel>(null)
  const boardSubDefault = useMemo(() => migrateMemberBoardSub(), [])
  const [boardSub, setBoardSub] = usePersistedState<MemberBoardSubTab>('member.boardSub', boardSubDefault)
  const aiSubDefault = useMemo(() => migrateMemberAiSubStorage(), [])
  const [aiSub, setAiSub] = usePersistedState<MemberAiSubTab>('member.aiSub', aiSubDefault)
  const [collaborationSub, setCollaborationSub] = usePersistedState<MemberCollaborationSubTab>('member.collaborationSub', 'CHAT')

  useEffect(() => {
    startSyncWorker()
    return () => stopSyncWorker()
  }, [])

  useEffect(() => {
    if (!currentUser?.uid) return
    void registerMobilePushToken(currentUser.uid, 'MEMBER')
  }, [currentUser?.uid])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.location.pathname === buildMemberBasePath(workspace)) {
      window.history.replaceState(null, '', buildMemberPath({ workspace, tab: 'for-you' }))
    }
  }, [workspace])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handlePopState = () => {
      setQueryState(new URLSearchParams(window.location.search))
      const checklistLink = parseChecklistDeepLink(window.location)
      if (checklistLink) {
        openChecklist(checklistLink.itemId, undefined, checklistLink.periodKey)
        try {
          const url = new URL(window.location.href)
          url.searchParams.delete('item')
          url.searchParams.delete('day')
          url.searchParams.delete('week')
          url.searchParams.delete('ws')
          url.searchParams.delete('workspaceId')
          const nextSearch = url.searchParams.toString()
          const nextUrl = url.pathname + (nextSearch ? `?${nextSearch}` : '') + url.hash
          window.history.replaceState(window.history.state, '', nextUrl)
          setQueryState(new URLSearchParams(window.location.search))
        } catch {
          // ignore
        }
      }
    }
    handlePopState()
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [openChecklist])

  const pushMemberPath = useCallback((path: string) => {
    if (typeof window === 'undefined') return
    window.history.pushState(null, '', path)
    setQueryState(new URLSearchParams(window.location.search))
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [])

  const effectiveBoardSub: MemberBoardSubTab = moduleHidden.memberBoard.has(boardSub) ? 'STRIPBOARD' : boardSub
  const rawCollabSub = route.collaborationSub ?? collaborationSub
  const effectiveCollaborationSub: MemberCollaborationSubTab = moduleHidden.memberNexus.has(rawCollabSub) ? 'CHAT' : rawCollabSub

  const checklistDeepLink = parseChecklistDeepLink(typeof window !== 'undefined' ? window.location : { search: '' })
  const highlightedNodeId = queryState.get('node')
  const checklistItemId = checklistDeepLink?.itemId ?? queryState.get('checklistItem') ?? (queryState.get('checklist') === '1' ? highlightedNodeId : null)
  const checklistPeriodKey = checklistDeepLink?.periodKey ?? null
  const checklistSectionProp = (queryState.get('section') as BlockerCtaSection | null) ?? null
  const rawPendingStatus = queryState.get('pendingStatus')
  const pendingStatusProp: ChecklistWorkflowStatus | null = rawPendingStatus && rawPendingStatus in STATUS_META ? (rawPendingStatus as ChecklistWorkflowStatus) : null
  const visibleChecklistItems = useMemo(
    () => checklistItems.filter((item) => !isChecklistGate(item)),
    [checklistItems],
  )
  const checklistItem = checklistItemId ? (visibleChecklistItems.find((item) => item.id === checklistItemId) ?? null) : null
  const overlayChecklistItem = overlayTop?.kind === 'checklist' ? (visibleChecklistItems.find((item) => item.id === overlayTop.id) ?? null) : null
  const activeChecklistItem = overlayChecklistItem ?? checklistItem
  const isOverlayChecklist = Boolean(overlayChecklistItem && overlayTop?.kind === 'checklist')

  useEffect(() => {
    if (overlayTop?.kind === 'node') {
      const isReadableNode = elements.some((element) => element.id === overlayTop.id) && !pathOnlyElementIds.has(overlayTop.id)
      if (isReadableNode) return
      closeTop()
      showToast(t('NLSItemNotAvailable'), 'warning')
      return
    }

    if (overlayTop?.kind === 'checklist') {
      if (checklistsLoaded !== true) return
      const isVisibleChecklist = visibleChecklistItems.some((item) => item.id === overlayTop.id)
      if (isVisibleChecklist) return
      closeTop()
      showToast(t('NLSItemNotAvailable'), 'warning')
    }
  }, [checklistsLoaded, closeTop, elements, overlayTop, pathOnlyElementIds, showToast, t, visibleChecklistItems])
  const workspaceChecklist = visibleChecklistItems.filter((item) => item.workspaceId === workspace.id)
  const pendingVouches = workspaceChecklist.filter((item) => (item.isCompleted || item.workflowStatus === 'COMPLETED') && typeof item.assigneeId === 'string' && item.assigneeId !== currentUser?.uid)

  const sidecar = useMemo(
    () => (
      <div className="memberSidecarPanel">
        <span className="memberEyebrow">{t('NLSMemberContextEyebrow')}</span>
        <h2>{selectedElement?.title ?? getMemberTabLabel(activeTab, t)}</h2>
        <p>{selectedElement?.description || t('NLSMemberContextFallback')}</p>
        <dl>
          <div>
            <dt>{t('NLSMemberLabelWorkspace')}</dt>
            <dd>{workspace.name}</dd>
          </div>
          <div>
            <dt>{t('NLSMemberLabelMode')}</dt>
            <dd>{t('NLSMemberModeMember')}</dd>
          </div>
          {selectedElement ? (
            <>
              <div>
                <dt>{t('NLSMemberLabelType')}</dt>
                <dd>{selectedElement.type}</dd>
              </div>
              <div>
                <dt>{t('NLSMemberLabelStatus')}</dt>
                <dd>{selectedElement.boardStatus ?? selectedElement.workflowStatus ?? 'TODO'}</dd>
              </div>
            </>
          ) : null}
        </dl>
      </div>
    ),
    [activeTab, selectedElement, t, workspace.name],
  )

  const navigate = useCallback((_tab: MemberTabKey, path: string) => {
    if (_tab === 'you') {
      setProfileOverlayOpen(true)
      return
    }
    if (typeof window !== 'undefined' && window.location.pathname !== path) {
      window.history.pushState(null, '', path)
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }, [])

  const [exitConfirmOpen, setExitConfirmOpen] = useState(false)
  const focusMode = useFocusMode()
  const openFocusTodos = useCallback(() => navigate('for-you', buildMemberPath({ workspace, tab: 'for-you' })), [navigate, workspace])

  useNativeBackButton({
    onEmpty: () => {
      if (focusMode.active) {
        focusMode.exit()
        return
      }
      if (activeTab !== 'for-you') {
        navigate('for-you', buildMemberPath({ workspace, tab: 'for-you' }))
        return
      }
      setExitConfirmOpen(true)
    },
  })

  const memberUid = currentUser?.uid
  const isOwner = useMemo(
    () => Boolean(memberUid && isWorkspaceOwner(workspace, memberUid)) || activeRole === 'OWNER',
    [workspace, memberUid, activeRole],
  )

  useEffect(() => {
    if (!workspace.id) return
    return subscribePageVisibility(workspace.id, currentUser?.uid, isOwner, activeRole)
  }, [workspace.id, currentUser?.uid, isOwner, activeRole])

  const isPageHidden = usePageVisibility()

  useEffect(() => {
    if (activeTab && isPageHidden(activeTab)) {
      queueMicrotask(() => {
        navigate('for-you', buildMemberPath({ workspace, tab: 'for-you' }))
        showToast(t('NLSControlNoticePageHidden'), 'info')
      })
    }
  }, [activeTab, isPageHidden, workspace, navigate, showToast, t])

  const openBoardNode = (elementId: string, openChecklist = false) => {
    if (pathOnlyElementIds.has(elementId)) return
    pushMemberPath(buildMemberBoardNodePath(workspace.slug, elementId, openChecklist))
  }

  const openChecklistItem = (checklistId: string, parentElementId?: string | null, opts?: { pendingStatus?: ChecklistWorkflowStatus; targetSection?: BlockerCtaSection }) => {
    pushMemberPath(buildMemberChecklistItemPath(workspace.slug, checklistId, parentElementId, opts?.pendingStatus, opts?.targetSection))
  }

  const applyGhostEcho = useCallback(
    (context: ViewContext) => {
      const targetWorkspace = workspaces.find(
        (candidate) => candidate.id === context.workspaceId && (isTeamWorkspace(candidate) || (personalInMemberAppEnabled && isPersonalWorkspace(candidate) && candidate.ownerId === currentUser?.uid)),
      )
      if (!targetWorkspace || !canPerform('timeline.view')) return 'UNAVAILABLE' as const
      if (context.kind === 'CHAT') {
        setCollaborationSub('CHAT')
        pushMemberPath(buildMemberPath({ workspace: targetWorkspace, tab: 'nexus' }))
        return 'APPLIED' as const
      }
      if (context.taskId && targetWorkspace.id === workspace.id && !elements.some((element) => element.id === context.taskId)) {
        return 'UNAVAILABLE' as const
      }
      const suffix = context.taskId ? `?node=${encodeURIComponent(context.taskId)}${context.checklistOpen ? '&checklist=1' : ''}` : ''
      pushMemberPath(buildMemberPath({ workspace: targetWorkspace, tab: 'board', query: suffix }))
      return 'APPLIED' as const
    },
    [canPerform, currentUser?.uid, elements, personalInMemberAppEnabled, pushMemberPath, setCollaborationSub, workspace.id, workspaces],
  )

  const handleSelectWorkspace = (next: Workspace) => {
    if (next.id === workspace.id) return
    const targetRole = rolesByWorkspaceId?.get(next.id) ?? (next.ownerId === currentUser?.uid ? 'OWNER' : 'VIEWER')
    const landing = resolveSwitchLanding({
      app: 'MEMBER',
      current: { tab: activeTab, subTab: collaborationSub },
      target: next,
      role: targetRole,
    })
    void requestWorkspaceSwitch({
      toWorkspaceId: next.id,
      source: 'USER',
      landing,
    })
  }

  const notificationNavigators: Omit<NotificationRouterCtx, 'workspaceSlug' | 'openIncomingInvites' | 'openMemberSettings' | 'openProfile' | 'openOverview'> & {
    missingNavigators: Array<'openCreditsLedger' | 'openReport'>
  } = {
    openBoardNode: (nodeId) => openNode(nodeId),
    openChecklistItem: (itemId, periodKey) => {
      openChecklist(itemId, undefined, periodKey)
    },
    openChatThread: (threadId?: string) => {
      setCollaborationSub('CHAT')
      pushMemberPath(buildMemberPath({ workspace, tab: 'nexus', query: threadId ? `?channel=${encodeURIComponent(threadId)}` : undefined }))
    },
    openMeeting: (meetingId?: string) => {
      if (switchedOffModules.has('meetings')) {
        notifyModuleOff('meetings')
        return
      }
      setCollaborationSub('MEETINGS')
      pushMemberPath(buildMemberPath({ workspace, tab: 'nexus', query: meetingId ? `?meeting=${encodeURIComponent(meetingId)}` : undefined }))
    },
    openForm: (formId?: string) => {
      if (switchedOffModules.has('forms')) {
        notifyModuleOff('forms')
        return
      }
      pushMemberPath(buildMemberPath({ workspace, tab: 'forms', query: formId ? `?form=${encodeURIComponent(formId)}` : undefined }))
    },
    openRaid: (raidId?: string) => {
      if (switchedOffModules.has('funPack')) {
        notifyModuleOff('funPack')
        return
      }
      void raidId
      setRaidOverlayOpen(true)
    },
    openAchievement: () => {
      if (switchedOffModules.has('funPack')) {
        notifyModuleOff('funPack')
        return
      }
      setProfileOverlayOpen(true)
    },
    openCreditsLedger: () => undefined,
    openReport: () => undefined,
    openAiHistory: (jobId?: string) => {
      setAiSub('HISTORY')
      pushMemberPath(buildMemberPath({ workspace, tab: 'ai', query: jobId ? `?job=${encodeURIComponent(jobId)}` : undefined }))
    },
    goto: (url) => pushMemberPath(url),
    missingNavigators: ['openCreditsLedger', 'openReport'],
  }

  const handleNativeWidgetIntent = useCallback(
    (intent: NativeWidgetIntent) => {
      const resolution = resolveMemberNativeWidgetIntent(intent, workspace.slug, { activeWorkspaceId: workspace.id })
      if (resolution.kind === 'NAVIGATE') {
        if (resolution.aiSub) setAiSub(resolution.aiSub)
        pushMemberPath(resolution.path)
      }
      if (resolution.kind === 'OPEN_WIDGET_PANEL') {
        setWidgetPanel(resolution.panel)
      }
    },
    [pushMemberPath, setAiSub, workspace.id, workspace.slug],
  )

  useNativeWidgetIntent(handleNativeWidgetIntent)

  const boardSubItems = useMemo(
    () => getBoardSubItems(t).filter((item) => !moduleHidden.memberBoard.has(item.key)),
    [moduleHidden.memberBoard, t],
  )
  const aiSubItems = useMemo(() => getAiSubItems(t), [t])
  const safeAiSub: MemberAiSubTab = isMemberAiSubTab(aiSub) ? aiSub : 'CREATE'
  useEffect(() => {
    if (safeAiSub !== aiSub) setAiSub(safeAiSub)
  }, [aiSub, safeAiSub, setAiSub])
  const nexusCounts = useNexusCounts(isMobile ? workspace.id : null, { skipMeetings: !showMeetingsInNexus })
  const collaborationSubItems = useMemo(
    () => getCollaborationSubItems(t, showMeetingsInNexus, nexusCounts.inHuddle),
    [nexusCounts.inHuddle, showMeetingsInNexus, t],
  )
  const openNexusSub = useCallback(
    (sub: MemberCollaborationSubTab) => {
      setCollaborationSub(sub)
      pushMemberPath(buildMemberPath({ workspace, tab: 'nexus' }))
    },
    [pushMemberPath, setCollaborationSub, workspace],
  )
  const chatNexusChips = useMemo<MemberNexusChip[]>(
    () => [
      { key: 'today', label: t('NLSMemberNexusToday'), value: nexusCounts.eventsToday, onClick: () => openNexusSub('CALENDAR') },
      ...(showMeetingsInNexus
        ? [
            {
              key: 'in-huddle',
              label: t('NLSMemberNexusInHuddle'),
              value: nexusCounts.inHuddle,
              tone: nexusCounts.inHuddle ? ('live' as const) : ('neutral' as const),
              onClick: () => openNexusSub('HUDDLE'),
            },
          ]
        : []),
    ],
    [nexusCounts.eventsToday, nexusCounts.inHuddle, openNexusSub, showMeetingsInNexus, t],
  )
  const calendarNexusChips = useMemo<MemberNexusChip[]>(
    () => [
      { key: 'unread', label: t('NLSMemberNexusUnread'), value: nexusCounts.unreadThreads, onClick: () => openNexusSub('CHAT') },
      ...(showMeetingsInNexus
        ? [
            {
              key: 'in-huddle',
              label: t('NLSMemberNexusInHuddle'),
              value: nexusCounts.inHuddle,
              tone: nexusCounts.inHuddle ? ('live' as const) : ('neutral' as const),
              onClick: () => openNexusSub('HUDDLE'),
            },
          ]
        : []),
    ],
    [nexusCounts.inHuddle, nexusCounts.unreadThreads, openNexusSub, showMeetingsInNexus, t],
  )
  const meetingsNexusChips = useMemo<MemberNexusChip[]>(
    () => [
      { key: 'unread', label: t('NLSMemberNexusUnread'), value: nexusCounts.unreadThreads, onClick: () => openNexusSub('CHAT') },
      { key: 'today', label: t('NLSMemberNexusToday'), value: nexusCounts.eventsToday, onClick: () => openNexusSub('CALENDAR') },
    ],
    [nexusCounts.eventsToday, nexusCounts.unreadThreads, openNexusSub, t],
  )
  const actor = useMemo(
    () => (currentUser ? { id: currentUser.uid, name: memberName || currentUser.email || t('NLSMemberModeMember') } : null),
    [currentUser, memberName, t],
  )
  const chatActionItems = visibleChecklistItems
    .filter((item) => item.workspaceId === workspace.id && item.assigneeId)
    .map((item) => ({
      id: item.id,
      title: item.title,
      subtitle: item.workflowStatus ?? item.boardStatus ?? null,
    }))
  const selectedChatItem = chatActionItemId
    ? (visibleChecklistItems.find((item) => item.id === chatActionItemId) ?? null)
    : (visibleChecklistItems.find((item) => item.workspaceId === workspace.id && item.assigneeId) ?? null)

  const requireChatActionItem = () => {
    if (!selectedChatItem || !actor) {
      showToast(t('NLSHelperChatFocusRequired'), 'ghost')
      return null
    }
    return selectedChatItem
  }

  const { scanStock } = useStockScan({
    workspaceId: scannerItem?.workspaceId ?? (workspace?.id || null),
    actor: actor ? { id: actor.id, name: actor.name, role: 'MEMBER' } : null,
    mode: 'member',
  })

  const handleInventoryScan = useCallback(
    async (barcode: string, direction: StockScanDirection = 'OUT') => {
      if (!actor || !scannerItem) return
      const controller = new AbortController()
      emitSensoryEvent('MUTATION_TRIGGERED', { source: 'member-inventory-scan', targetId: scannerItem.id }, { signal: controller.signal })
      try {
        const result = await scanStock(barcode, direction, {
          taskId: scannerItem.parentContainerId ?? '',
          checklistItemId: scannerItem.id,
        })
        setScannerItem(null)
        emitSensoryEvent('SCAN_SUCCESS', { source: 'member-inventory-scan', targetId: scannerItem.id }, { signal: controller.signal })
        showToast(`${result.item.name} ${result.action === 'IN' ? 'scanned in' : 'scanned out'}.`, 'ghost')
      } catch (error) {
        controller.abort()
        emitSensoryEvent('SCAN_ERROR', { source: 'member-inventory-scan', targetId: scannerItem.id })
        const message =
          error instanceof ResourceMismatchError
            ? error.message
            : error instanceof InventoryScanError && error.code === 'INSUFFICIENT_STOCK'
              ? error.message
              : error instanceof Error
                ? error.message
                : t('NLSHelperToastInventoryScanFailed')
        showToast(message, 'error')
      }
    },
    [actor, scanStock, scannerItem, showToast, t],
  )

  const { timeZone: memberWorkspaceTimeZone } = useWorkspaceDay(workspace?.id)

  // Recurring items' quick actions target the CURRENT occurrence — the
  // member picked this item off a live feed of "what's actionable now",
  // so today's date (DAILY) or this week (WEEKLY) is the intended target.
  // Passing pageKey survives a queue drained after midnight rollover.
  const pageKeyForChatItem = (item: ChecklistItem | null): string | null => {
    if (!item) return null
    if (item.checklistType !== 'DAILY' && item.checklistType !== 'WEEKLY') return null
    return currentOccurrenceKeyForItem(item, new Date(), memberWorkspaceTimeZone)
  }

  const memberChatQuickActions = {
    focusedLabel: selectedChatItem?.title,
    items: chatActionItems,
    selectedItemId: selectedChatItem?.id ?? null,
    onSelectItem: setChatActionItemId,
    onEvidence: async (file: File) => {
      const item = requireChatActionItem()
      if (!item || !actor) return
      await queueChecklistProof(
        {
          workspaceId: item.workspaceId,
          checklistItemId: item.id,
          fileName: file.name || 'evidence',
          contentType: file.type || 'application/octet-stream',
          actorId: actor.id,
          actorName: actor.name,
          assignedAt: null,
          pageKey: pageKeyForChatItem(item),
          actorRole: 'MEMBER',
        },
        file,
      )
      showToast(t('NLSHelperToastEvidenceQueued'), 'success')
    },
    onScan: () => {
      const item = requireChatActionItem()
      if (item) setScannerItem(item)
    },
    onDone: async () => {
      const item = requireChatActionItem()
      if (!item || !actor) return
      await queueChecklistToggle({
        workspaceId: item.workspaceId,
        goalId: item.parentContainerId ?? '',
        taskId: null,
        subTaskId: null,
        checklistItemId: item.id,
        isCompleted: true,
        workflowStatus: 'COMPLETED',
        actorId: actor.id,
        actorName: actor.name,
        assignedAt: null,
        pageKey: pageKeyForChatItem(item),
        actorRole: 'MEMBER',
      })
      showToast(t('NLSHelperToastDoneQueued'), 'success')
    },
    onComment: async (body: string) => {
      const item = requireChatActionItem()
      if (!item || !actor) return
      await queueChecklistComment({
        workspaceId: item.workspaceId,
        checklistItemId: item.id,
        parentContainerId: item.parentContainerId ?? item.id,
        body,
        actorId: actor.id,
        actorName: actor.name,
        assignedAt: null,
        pageKey: pageKeyForChatItem(item),
        actorRole: 'MEMBER',
      })
      showToast(t('NLSHelperToastCommentQueued'), 'success')
    },
  }

  const confirmBroadcast = () => {
    setWidgetPanel(null)
    showToast('Broadcast alert confirm received. Delivery wiring will use the priority notification backend.', 'ghost')
  }

  const secondaryTabs = !isMobile ? undefined : activeTab === 'board' ? (
    <MobileTabRow
      compact
      items={boardSubItems}
      activeKey={effectiveBoardSub}
      onChange={setBoardSub}
      ariaLabel={t('NLSMemberAriaBoardSections')}
      testId="member-board-subtabs"
      className="py-1"
      placement="bottom"
      overflowStrategy="scroll"
    />
  ) : activeTab === 'nexus' || activeTab === 'calendar' || activeTab === 'chat' ? (
    <MobileTabRow
      compact
      items={collaborationSubItems}
      activeKey={effectiveCollaborationSub}
      onChange={(key) => {
        setCollaborationSub(key)
        const nexusPath = buildMemberPath({ workspace, tab: 'nexus' })
        if (window.location.pathname !== nexusPath) pushMemberPath(nexusPath)
      }}
      ariaLabel={t('NLSMemberAriaNexusSections')}
      testId="member-nexus-subtabs"
      placement="bottom"
      overflowStrategy="scroll"
    />
  ) : activeTab === 'ai' ? (
    <MobileTabRow
      compact
      items={aiSubItems}
      activeKey={safeAiSub}
      onChange={setAiSub}
      ariaLabel={t('NLSMemberAriaAiSections')}
      testId="member-ai-subtabs"
      placement="bottom"
      overflowStrategy="scroll"
    />
  ) : undefined

  return (
    <>
      <KeyboardHeightBridge />
      <MemberDashboardLayout
        workspace={workspace}
        role={activeRole}
        activeTab={activeTab}
        aiMode={safeAiSub}
        nexusPanel={effectiveCollaborationSub}
        isProfileOpen={profileOverlayOpen}
        sidecar={sidecar}
        secondaryTabs={secondaryTabs}
        onNavigate={navigate}
        onSelectWorkspace={handleSelectWorkspace}
        onOpenProfile={() => setProfileOverlayOpen(true)}
        notificationNavigators={notificationNavigators}
        onOpenSyncCenter={() => {
          navigate('sync', buildMemberPath({ workspace, tab: 'sync' }))
        }}
      >
        <MemberSurfaceTheme />
        <MemberWidgetCachePublisher workspace={workspace} />
        <GhostEchoListener onApply={applyGhostEcho} />
        <RaidCelebration workspaceId={switchedOffModules.has('funPack') ? null : workspace.id} />
        {!switchedOffModules.has('funPack') && (
          <RaidOverlay open={raidOverlayOpen} onClose={() => setRaidOverlayOpen(false)} />
        )}
        {activeTab === 'for-you' ? (
          <TabErrorBoundary tabName={getMemberTabLabel('for-you', t)}>
            {isMobile ? (
              <MemberTodos workspace={workspace} onOpenChecklistItem={(item) => openChecklistItem(item.id, item.parentContainerId)} onOpenTodos={openFocusTodos} />
            ) : (
              <ForYouPage onOpenRaid={!switchedOffModules.has('funPack') && canPerform('raid.participate') ? () => setRaidOverlayOpen(true) : undefined} />
            )}
          </TabErrorBoundary>
        ) : activeTab === 'board' ? (
          <TabErrorBoundary tabName={getMemberTabLabel('board', t)} resetKey={effectiveBoardSub}>
            {isMobile ? (
              <MemberBoardHub
                sub={effectiveBoardSub}
                highlightedNodeId={highlightedNodeId}
                onOpenElement={setSelectedElement}
                onOpenChecklistItem={(item, opts) => openChecklistItem(item.id, item.parentContainerId, opts)}
                onOpenBoardNode={(elementId) => openBoardNode(elementId)}
                onOpenChat={() => {
                  setCollaborationSub('CHAT')
                  navigate('nexus', buildMemberPath({ workspace, tab: 'nexus' }))
                }}
                onNavigateAnalysisTarget={(target) => {
                  if (target === 'MEETINGS') {
                    if (switchedOffModules.has('meetings')) {
                      notifyModuleOff('meetings')
                      return
                    }
                    setCollaborationSub('MEETINGS')
                    navigate('nexus', buildMemberPath({ workspace, tab: 'nexus' }))
                  } else if (target === 'AUTOMATE') {
                    if (switchedOffModules.has('automate')) {
                      notifyModuleOff('automate')
                      return
                    }
                    navigate('automations', buildMemberPath({ workspace, tab: 'automations' }))
                  } else if (target === 'AI_MAGIC') {
                    navigate('ai', buildMemberPath({ workspace, tab: 'ai' }))
                  } else if (target === 'REPORTS') {
                    showToast(t('NLSAnalysisMobileReportsUnavailable'), 'ghost')
                  } else {
                    setBoardSub('STRIPBOARD')
                    navigate('board', buildMemberPath({ workspace, tab: 'board' }))
                  }
                }}
              />
            ) : (
              <MemberBoard highlightedNodeId={highlightedNodeId} onOpenElement={setSelectedElement} onOpenChecklistItem={(item, opts) => openChecklistItem(item.id, item.parentContainerId, opts)} />
            )}
          </TabErrorBoundary>
        ) : activeTab === 'ai' ? (
          <TabErrorBoundary tabName={getMemberTabLabel('ai', t)}>
            <AiMagicController
              role={activeRole}
              chrome={isMobile ? 'mobile' : 'desktop'}
              section={isMobile ? safeAiSub : undefined}
              onRequestSection={setAiSub}
              onOpenBoardDashboard={() => {
                setBoardSub('STRIPBOARD')
                pushMemberPath(buildMemberPath({ workspace, tab: 'board' }))
              }}
            />
          </TabErrorBoundary>
        ) : activeTab === 'nexus' || activeTab === 'calendar' || activeTab === 'chat' ? (
          <TabErrorBoundary tabName={getMemberTabLabel('nexus', t)}>
            {effectiveCollaborationSub === 'CALENDAR' ? (
              isMobile ? (
                <MemberCalendarScreen
                  workspaceId={workspace.id}
                  readOnly={!canPerformOnNode('timeline.edit', null)}
                  canScheduleMeetings={canPerform('meetings.schedule')}
                  workspaceOwner={{ id: workspace.ownerId, name: t('NLSMemberWorkspaceOwner') }}
                  personalWorkspace={isPersonalWorkspace(workspace)}
                  nexusChips={calendarNexusChips}
                  onOpenElement={(elementId) => openBoardNode(elementId)}
                  onOpenChecklistItem={(parentElementId, nextChecklistItemId) => openChecklistItem(nextChecklistItemId, parentElementId)}
                  onOpenChat={() => openNexusSub('CHAT')}
                  onOpenMeetings={() => openNexusSub('MEETINGS')}
                  onOpenHuddle={() => openNexusSub('HUDDLE')}
                />
              ) : (
                <CalendarPage
                  activeBoard="DASHBOARD"
                  readOnly
                  onOpenElement={(elementId) => openBoardNode(elementId)}
                  onOpenChecklistItem={(parentElementId, nextChecklistItemId) => openChecklistItem(nextChecklistItemId, parentElementId)}
                />
              )
            ) : effectiveCollaborationSub === 'HUDDLE' && isMobile ? (
              <MemberHuddleScreen workspaceId={workspace.id} nexusChips={meetingsNexusChips} onOpenChat={() => openNexusSub('CHAT')} />
            ) : effectiveCollaborationSub === 'MEETINGS' || effectiveCollaborationSub === 'HUDDLE' ? (
              isMobile ? (
                <MemberMeetingsScreen
                  workspaceId={workspace.id}
                  canScheduleMeetings={canPerform('meetings.schedule')}
                  workspaceOwner={{ id: workspace.ownerId, name: t('NLSMemberWorkspaceOwner') }}
                  personalWorkspace={isPersonalWorkspace(workspace)}
                  nexusChips={meetingsNexusChips}
                  onOpenChat={() => openNexusSub('CHAT')}
                  onOpenCalendar={() => openNexusSub('CALENDAR')}
                  onOpenHuddle={() => openNexusSub('HUDDLE')}
                  onOpenBoardNode={(elementId) => openBoardNode(elementId)}
                />
              ) : (
                <MemberBoardHub
                  sub="MEETINGS"
                  highlightedNodeId={highlightedNodeId}
                  onOpenElement={setSelectedElement}
                  onOpenChecklistItem={(item) => openChecklistItem(item.id, item.parentContainerId)}
                  onOpenBoardNode={(elementId) => openBoardNode(elementId)}
                  onOpenChat={() => {
                    setCollaborationSub('CHAT')
                    navigate('nexus', buildMemberPath({ workspace, tab: 'nexus' }))
                  }}
                  onNavigateAnalysisTarget={() => undefined}
                />
              )
            ) : isMobile ? (
              <MemberChatScreen
                onElementMentionClick={canPerform('timeline.view') ? (elementId) => openBoardNode(elementId) : undefined}
                helperQuickActions={memberChatQuickActions}
                nexusChips={chatNexusChips}
                onOpenAiMagic={() => {
                  setAiSub('CREATE')
                  navigate('ai', buildMemberPath({ workspace, tab: 'ai' }))
                }}
              />
            ) : (
              <ChatPage platform="member" onElementMentionClick={canPerform('timeline.view') ? (elementId) => openBoardNode(elementId) : undefined} helperQuickActions={memberChatQuickActions} />
            )}
          </TabErrorBoundary>
        ) : activeTab === 'forms' ? (
          <TabErrorBoundary tabName={getMemberTabLabel('forms', t)}>
            {isMobile ? (
              <MemberFormsMobile workspaceId={workspace.id} role={activeRole} />
            ) : (
              <FormsOrchestrator workspaceId={workspace.id} role={activeRole} />
            )}
          </TabErrorBoundary>
        ) : activeTab === 'sync' ? (
          <TabErrorBoundary tabName={getMemberTabLabel('sync', t)}>
            <MobileSyncCenter
              role="MEMBER"
              onOpenItem={(itemId) => openChecklistItem(itemId)}
              compact={isMobile}
            />
          </TabErrorBoundary>
        ) : activeTab === 'automations' ? (
          <TabErrorBoundary tabName={getMemberTabLabel('automations', t)}>
            {isMobile ? (
              <MemberAutomationsMobile
                role={activeRole}
                onOpenMembers={() => navigate('team', buildMemberPath({ workspace, tab: 'team' }))}
              />
            ) : (
              <AutomationLauncher role={activeRole} />
            )}
          </TabErrorBoundary>
        ) : activeTab === 'team' ? (
          <TabErrorBoundary tabName={getMemberTabLabel('team', t)}>
            <div className="memberControlPanel">
              {isMobile ? (
                <MemberPeopleMobile workspace={workspace} />
              ) : (
                <InvitesHubV2 isMobile={false} />
              )}
            </div>
          </TabErrorBoundary>
        ) : activeTab === 'ops' ? (
          <TabErrorBoundary tabName={getMemberTabLabel('ops', t)}>
            <MemberOpsTab
              workspaceId={workspace.id}
              requestId={queryState.get('request') || undefined}
            />
          </TabErrorBoundary>
        ) : activeTab === 'class' ? (
          <TabErrorBoundary tabName={getMemberTabLabel('class', t)}>
            <ClassTab
              workspace={workspace}
              role={activeRole}
              userId={currentUser?.uid}
              isMobile={isMobile}
            />
          </TabErrorBoundary>
        ) : activeTab === 'bookings' ? (
          <TabErrorBoundary tabName={getMemberTabLabel('bookings', t)}>
            <MemberBookingsView
              workspace={workspace}
              role={activeRole}
              isMobile={isMobile}
            />
          </TabErrorBoundary>
        ) : activeTab === 'you' ? null : (
          <MemberPageComposite workspace={workspace} role={activeRole} />
        )}
        {profileOverlayOpen ? (
          <SideNavPageShell
            title={t('NLSMemberPanelProfile')}
            eyebrow={t('NLSMemberPanelProfileEyebrow')}
            uiName="UI-MPCAP-MPRO"
            screenGuideId="member.profile"
            onClose={() => setProfileOverlayOpen(false)}
          >
            <ProfileDetailsPage />
            <MemberAppearanceSection />
          </SideNavPageShell>
        ) : null}
        {widgetPanel === 'BROADCAST' ? (
          <MobileFullScreenPanel title={t('NLSMemberWidgetBroadcastTitle')} eyebrow={t('NLSMemberWidgetCommandEyebrow')} onClose={() => setWidgetPanel(null)}>
            <section className="mobileSettingsCommandCenter">
              <div className="mobileSettingsIdentityCard">
                <div>
                  <span className="memberEyebrow">{t('NLSMemberWidgetPriorityPing')}</span>
                  <h2>{t('NLSMemberWidgetBroadcastConfirmTitle')}</h2>
                  <p>{t('NLSMemberWidgetBroadcastBody')}</p>
                </div>
              </div>
              <div className="mobileSettingsActionGrid">
                <Button variant="primary" size="lg" onClick={confirmBroadcast}>
                  {t('NLSMemberWidgetBroadcastConfirmAction')}
                </Button>
                <Button variant="tertiary" size="md" onClick={() => setWidgetPanel(null)}>
                  {t('NLSCommonActionCancel')}
                </Button>
              </div>
            </section>
          </MobileFullScreenPanel>
        ) : null}
        {widgetPanel === 'VOUCH' ? (
          <MobileFullScreenPanel title={t('NLSMemberWidgetQuickVouchTitle')} eyebrow={t('NLSMemberWidgetCommandEyebrow')} onClose={() => setWidgetPanel(null)}>
            <section className="mobileSettingsCommandCenter">
              <div className="mobileSettingsIdentityCard">
                <div>
                  <span className="memberEyebrow">{t('NLSMemberWidgetPendingVerification')}</span>
                  <h2>{t(pendingVouches.length === 1 ? 'NLSMemberWidgetVouchCountOne' : 'NLSMemberWidgetVouchCount', { count: pendingVouches.length })}</h2>
                  <p>{t('NLSMemberWidgetVouchBody')}</p>
                </div>
              </div>
              <div className="mobileSettingsStack">
                {pendingVouches.slice(0, 8).map((item) => (
                  <Pressable
                    key={item.id}
                    className="pendingInviteRow"
                    onClick={() => {
                      setWidgetPanel(null)
                      openChecklistItem(item.id, item.parentContainerId)
                    }}
                  >
                    <span>{item.title}</span>
                    <strong>{t('NLSCommonActionOpen')}</strong>
                  </Pressable>
                ))}
                {pendingVouches.length === 0 ? <p className="memberDetailHint">{t('NLSMemberWidgetNoVouches')}</p> : null}
              </div>
            </section>
          </MobileFullScreenPanel>
        ) : null}
        {widgetPanel === 'VOICE' ? (
          <MobileFullScreenPanel title={t('NLSMemberWidgetWhisperTitle')} eyebrow={t('NLSMemberWidgetCommandEyebrow')} onClose={() => setWidgetPanel(null)}>
            <section className="mobileSettingsCommandCenter">
              <div className="mobileSettingsIdentityCard">
                <div>
                  <span className="memberEyebrow">{t('NLSMemberWidgetVoiceToTask')}</span>
                  <h2>{t('NLSMemberWidgetOpenAiComposerTitle')}</h2>
                  <p>{t('NLSMemberWidgetVoiceBody')}</p>
                </div>
              </div>
              <div className="mobileSettingsActionGrid">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => {
                    setWidgetPanel(null)
                    setAiSub('CREATE')
                    navigate('ai', buildMemberPath({ workspace, tab: 'ai' }))
                  }}
                >
                  {t('NLSMemberWidgetOpenComposerAction')}
                </Button>
              </div>
            </section>
          </MobileFullScreenPanel>
        ) : null}
        <BarcodeScannerShell open={Boolean(scannerItem)} onClose={() => setScannerItem(null)} onScan={(barcode, action) => void handleInventoryScan(barcode, action)} />
        {activeChecklistItem ? (
          <MemberChecklistDetailPage
            item={activeChecklistItem}
            pendingStatus={isOverlayChecklist ? null : pendingStatusProp}
            initialSection={isOverlayChecklist ? null : checklistSectionProp}
            initialOccurrenceKey={isOverlayChecklist ? (overlayTop?.kind === 'checklist' ? overlayTop.initialOccurrenceKey : null) : checklistPeriodKey}
            onOpenWorkspaceItem={(reference) => {
              if (reference.kind === 'checklist') {
                if (isOverlayChecklist) openChecklist(reference.id, reference.parentId)
                else openChecklistItem(reference.id, reference.parentId)
                return
              }
              if (reference.kind === 'goal' || reference.kind === 'task' || reference.kind === 'subtask') {
                if (isOverlayChecklist) openNode(reference.id)
                else openBoardNode(reference.id)
                return
              }
              if (import.meta.env.DEV) {
                console.warn(`[MemberChecklistDetailPage] No navigator is available for ${reference.kind}.`)
              }
            }}
            onClose={() => {
              if (isOverlayChecklist) {
                closeTop()
                return
              }
              const parentId = activeChecklistItem.parentContainerId
              if (parentId && !isSyntheticChecklistParent(parentId)) openBoardNode(parentId, false)
              else pushMemberPath(buildMemberPath({ workspace, tab: 'board' }))
            }}
          />
        ) : null}
        {overlayTop?.kind === 'node' && !pathOnlyElementIds.has(overlayTop.id) && elements.some((element) => element.id === overlayTop.id) ? (
          <MemberNodeDetailsSheet
            open
            onClose={closeTop}
            element={elements.find((element) => element.id === overlayTop.id) ?? null}
            checklist={null}
            canEdit={false}
            isSaving={false}
            hasPendingChanges={false}
            onFieldChange={() => {}}
            onSave={() => {}}
            onDiscard={() => {}}
            attachmentTarget={null}
            readOnlySummaryOnly
          />
        ) : null}
        <ExitConfirmSheet open={exitConfirmOpen} onClose={() => setExitConfirmOpen(false)} />
      </MemberDashboardLayout>
    </>
  )
}

export const AppMember = ({ route, workspace }: { route: MemberRoute; workspace: Workspace }) => {
  return (
    <XRayProvider>
      <BackStackProvider>
        <MemberOverlayProvider>
          <AppMemberInner route={route} workspace={workspace} />
        </MemberOverlayProvider>
      </BackStackProvider>
    </XRayProvider>
  )
}
